import { Flex } from '@mantine/core';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { ButtonValid, CardV2 as Card, EmptyState, Radios, Text } from '@feathr/components';
import { cssVar, getIconForAction } from '@feathr/hooks';
import { useFlags } from '@feathr/report_components';

import GeoFilter from './GeoFilter';
import GeoFilterModal from './GeoFilterModal';
import GeoFilterSet from './GeoFilterSet';
import { useLocations } from './Locations.hooks';
import { EGeoFilterMode, getGeoFilterModeOptions, getSaveCustomSetErrors } from './Locations.utils';
import SaveCustomSetModal from './SaveCustomSetModal';

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

function Locations({ campaign, targetings }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { isOmniCampaign } = campaign;
  const { hasSavedGeoFilterSets } = useFlags();

  const {
    geoFilterMode,
    defaultGeoSetId,
    hasUserDeclinedGeofilters,
    isSaving,
    filteredTargetings,
    remainingLocations,
    addFilterErrors,
    hasInvalidTargetings,
    modals,
    handleAddFilter,
    handleRemoveFilter,
    handleGeoFilterModeChange,
    handleSaveCustomSet,
    handleSaveCustomSetSubmit,
  } = useLocations({ campaign, targetings });

  const geofilterOptions = getGeoFilterModeOptions(t);
  const saveCustomSetErrors = getSaveCustomSetErrors(
    filteredTargetings.length,
    hasInvalidTargetings,
    t,
  );

  const addFilterButton = (
    <Flex align={'center'} gap={cssVar('--spacing-4')} key={'add-filter-section'}>
      <ButtonValid
        errors={addFilterErrors}
        id={'addFilter'}
        onClick={handleAddFilter}
        prefix={getIconForAction('add')}
      >
        {t('Add filter')}
      </ButtonValid>
      {hasSavedGeoFilterSets && filteredTargetings.length > 0 && (
        <Text.DefaultRegular>
          {t('{{count}} locations remaining', { count: remainingLocations })}
        </Text.DefaultRegular>
      )}
    </Flex>
  );

  const emptyState = (
    <EmptyState
      description={t('Add a filter to get started')}
      label={t('No filters added')}
      theme={'neutral'}
    >
      {addFilterButton}
    </EmptyState>
  );

  return (
    <Card width={isOmniCampaign ? 'full' : 'narrow'}>
      <Card.Header
        description={t(
          'Use Filters to include or exclude locations. Ads will only show to people in included areas and will not show to people in excluded areas. Filtering is optional but recommended.',
        )}
        isCollapsible={true}
        title={hasSavedGeoFilterSets ? t('Locations') : t('Filters')}
      />
      <Card.Content addVerticalGap={true}>
        {hasSavedGeoFilterSets ? (
          <>
            <Radios
              dataName={'geofilter-mode-selection'}
              layout={'block'}
              onChange={(newValue): void => handleGeoFilterModeChange(newValue as EGeoFilterMode)}
              options={geofilterOptions}
              orientation={'vertical'}
              value={geoFilterMode}
            />

            {geoFilterMode === EGeoFilterMode.SAVED_SET && (
              <GeoFilterSet
                campaign={campaign}
                defaultGeoSetId={defaultGeoSetId}
                hasUserDeclinedGeofilters={hasUserDeclinedGeofilters}
                targetings={targetings}
              />
            )}

            {geoFilterMode === EGeoFilterMode.CUSTOM_SET && (
              <>
                <Text.DefaultSemibold>{t('Locations')}</Text.DefaultSemibold>
                {filteredTargetings.length === 0
                  ? emptyState
                  : filteredTargetings.map((targeting, index) => (
                      <GeoFilter
                        campaign={campaign}
                        key={targeting.id || index}
                        layout={'stacked'}
                        onRemove={handleRemoveFilter}
                        targeting={targeting}
                        targetings={targetings}
                      />
                    ))}
                {filteredTargetings.length > 0 && addFilterButton}
              </>
            )}
          </>
        ) : (
          <>
            {filteredTargetings.length === 0
              ? emptyState
              : filteredTargetings.map((targeting, index) => (
                  <GeoFilter.V1
                    campaign={campaign}
                    key={targeting.id || index}
                    onRemove={handleRemoveFilter}
                    targeting={targeting}
                    targetings={targetings}
                  />
                ))}
            {filteredTargetings.length > 0 && addFilterButton}
          </>
        )}
      </Card.Content>
      {hasSavedGeoFilterSets && geoFilterMode === EGeoFilterMode.CUSTOM_SET && (
        <Card.Actions>
          <ButtonValid
            errors={saveCustomSetErrors}
            onClick={handleSaveCustomSet}
            prefix={getIconForAction('save')}
          >
            {t('Save custom set')}
          </ButtonValid>
        </Card.Actions>
      )}

      <SaveCustomSetModal
        isSaving={isSaving}
        onClose={modals.saveCustomSet.close}
        onSave={handleSaveCustomSetSubmit}
        opened={modals.saveCustomSet.isOpen}
      />

      <GeoFilterModal
        campaign={campaign}
        context={modals.editCreate.context}
        onClose={modals.editCreate.close}
        opened={modals.editCreate.isOpen}
      />
    </Card>
  );
}

export default observer(Locations);
